import { ChangeDetectorRef, ChangeDetectionStrategy, Component, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { FikoKutuk } from '../FikoKutuk';
import { Subscription } from 'rxjs';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';

declare var $: any;

@Component({
  selector: 'fikoyt-fiko-kutuk',
  templateUrl: './fiko-kutuk.component.html',
  styleUrls: ['./fiko-kutuk.component.scss'],
  imports: [CommonModule, FormsModule,],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FikoKutukComponent implements OnDestroy, AfterViewInit {
  id_dialog = this.FikoYT.fikoIdUret();
  subsList: Subscription[] = [];

  @Input() kutuk: FikoKutuk;
  @Input() pencere: any; // Dialog Core'dan gelen pencere referansı

  constructor(public readonly FikoYT: FikoYTService, public readonly CD: ChangeDetectorRef) {
    this.subsList.push(FikoYT.degisti.subscribe(() => {
      this.CD.markForCheck();
    }))
  }

  ngAfterViewInit() {
    // Dialog açılış kontrolü - yüzey seçimini başlatmıyoruz
    // Default eksen yönü (0,0,1) ile başlar, kullanıcı "Sıfırla" dediğinde seçim istenir
  }

  /**
   * parseFloat wrapper - template'de kullanım için
   */
  parseFloat(value: any): number {
    return parseFloat(value);
  }

  /**
   * Boyutları sıralı döndür: [en büyük, ortanca, en küçük]
   */
  getSortedSizes(): { large: number; medium: number; small: number } {
    if (!this.kutuk?.roughStockResult?.sizes) {
      return { large: 0, medium: 0, small: 0 };
    }

    const sizes = [
      this.kutuk.roughStockResult.sizes.x,
      this.kutuk.roughStockResult.sizes.y,
      this.kutuk.roughStockResult.sizes.z
    ].sort((a, b) => b - a);

    return {
      large: sizes[0],
      medium: sizes[1],
      small: sizes[2]
    };
  }

  /**
   * Kütük oluştur
   */
  roughStockOlustur() {
    if (!this.kutuk) {
      return;
    }

    this.kutuk.roughStockOlustur();
    this.FikoYT.degisti.emit();
  }

  /**
   * Seçilen cizim için stock oluştur
   */
  roughStockOlusturCizimIcin() {
    if (!this.kutuk) {
      return;
    }

    this.kutuk.roughStockOlusturCizimIcin();
    this.FikoYT.degisti.emit();
  }

  /**
   * Renk güncelle
   */
  renkGuncelle(newColor: string) {
    const colorValue = parseInt(newColor.substring(1), 16);
    this.kutuk.renkGuncelle(colorValue);
    this.FikoYT.degisti.emit();
  }

  /**
   * Opacity güncelle
   */
  opacityGuncelle(newOpacity: number) {
    this.kutuk.opacityGuncelle(newOpacity);
    this.FikoYT.degisti.emit();
  }

  /**
   * Stock'u kaldır
   */
  stockuKaldir() {
    this.kutuk.stockuKaldir();
    this.FikoYT.degisti.emit();
  }

  /**
   * 2 Eksen modunu toggle et
   */
  toggleTwoAxisMode() {
    // Mod değiştirmeden ÖNCE seçim durumunu kontrol et
    const hadSelection = this.kutuk.twoAxisMode
      ? this.kutuk.selectedAxes?.length > 0
      : this.kutuk.secilenFaceNormal != null;

    // Mod değiştir
    this.kutuk.twoAxisMode = !this.kutuk.twoAxisMode;

    if (hadSelection) {
      // Seçimi yeni modun formatına dönüştür
      if (this.kutuk.twoAxisMode) {
        // Normal → 2 Eksen: secilenFaceNormal'ı selectedAxes[0]'a kopyala
        if (this.kutuk.secilenFaceNormal) {
          this.kutuk.selectedAxes = [this.kutuk.secilenFaceNormal.clone()];
          this.kutuk.secilenFaceNormal = null;
        }
      } else {
        // 2 Eksen → Normal: selectedAxes[0]'ı secilenFaceNormal'a kopyala
        if (this.kutuk.selectedAxes?.length > 0) {
          this.kutuk.secilenFaceNormal = this.kutuk.selectedAxes[0].clone();
          this.kutuk.selectedAxes = [];
        }
      }
      // Seçim var, kullanıcı "Sıfırla" butonuna tıklayıncaya kadar yeni seçim başlatma
      this.kutuk.yuzeySecimiDurdur();
    } else {
      // Seçim yoktu, otomatik seçimi sıfırla ve yeni seçim başlat
      this.kutuk.resetAxisSelection();
      this.kutuk.yuzeySecimiBaslat();
    }

    this.FikoYT.degisti.emit();
    this.CD.markForCheck();
  }

  /**
   * Eksen seçimini sıfırla (2 eksen modunda)
   */
  resetAkseler() {
    this.kutuk.resetAxisSelection();

    // Yüzey seçimini tekrar başlat
    this.kutuk.yuzeySecimiBaslat();

    this.FikoYT.degisti.emit();
    this.CD.markForCheck();
  }

  /**
   * Yüzey seçimini sıfırla (alignToFace'i de null'a set et)
   */
  resetYuzeySecimi() {
    this.kutuk.secilenFaceNormal = null;
    this.kutuk.roughStockOptions.alignToFace = null;

    // Yüzey seçimini tekrar başlat
    this.kutuk.yuzeySecimiBaslat();

    this.FikoYT.degisti.emit();
    this.CD.markForCheck();
  }

  /**
   * Dialog kapat
   */
  public kapa() {
    // Dialog core'ın kapa metodunu çağır
    // FikoDialogCore'dan { instance: this } şeklinde gelir
    if (this.pencere?.instance && typeof this.pencere.instance.kapa === 'function') {
      try {
        this.pencere.instance.kapa();
        return;
      } catch (error) {
        // pencere.instance.kapa() hata
      }
    }

    // Fallback: direkt pencere.kapa() dene (eski pattern için)
    if (this.pencere && typeof this.pencere.kapa === 'function') {
      try {
        this.pencere.kapa();
        return;
      } catch (error) {
        // pencere.kapa() hata
      }
    }
  }

  ngOnDestroy(): void {
    // Dialog kapanınca yüzey seçimini durdur
    if (this.kutuk) {
      this.kutuk.yuzeySecimiDurdur();
    }

    $("#dialogFikoKutuk" + this.id_dialog).remove();
    this.subsList.forEach(element => {
      element.unsubscribe();
    });
  }
}


